/* Configurator — iframe embed, interactive card, action queue */

import { state, refs, esc, escAttr, scrollChat } from './state.js';
import { wsSend } from './ws.js';
import { expandZone } from './layout-manager.js';
import { showPanel, getPanelContainer } from './panel-registry.js';

// ---------------------------------------------------------------------------
// Embedded SAP CPQ Configurator (iframe)
// ---------------------------------------------------------------------------

export function createConfiguratorEmbed(configId, productName, partNumber) {
  const container = document.createElement("div");
  container.className = "cfg-embed";
  container.id = `cfg-embed-${configId}`;

  const header = document.createElement("div");
  header.className = "cfg-embed-header";
  header.innerHTML = `
    <div class="cfg-embed-title">
      <span class="cfg-gear-icon">&#9881;</span>
      <strong>${esc(productName)}</strong>
      ${partNumber ? `<span class="cfg-part-number">${esc(partNumber)}</span>` : ""}
    </div>
    <div class="cfg-embed-actions">
      <button class="btn btn-xs cfg-embed-pop" title="Open in new tab">&#8599;</button>
      <button class="btn btn-xs cfg-embed-close" title="Close">&#10005;</button>
    </div>
  `;
  container.appendChild(header);

  const iframe = document.createElement("iframe");
  iframe.className = "cfg-iframe";
  iframe.src = `/Configurator.aspx?configurationId=${encodeURIComponent(configId)}`;
  iframe.setAttribute("frameborder", "0");
  iframe.setAttribute("allowfullscreen", "true");
  container.appendChild(iframe);

  iframe.addEventListener("load", () => {
    container.classList.add("loaded");
  });

  header.querySelector(".cfg-embed-pop").addEventListener("click", () => {
    window.open(iframe.src, "_blank");
  });

  header.querySelector(".cfg-embed-close").addEventListener("click", () => {
    _cfgSendAction(configId, { action: "cancel" });
    container.classList.add("done");
    container.innerHTML = '<div class="cfg-done-msg cfg-cancelled">Configuration cancelled</div>';
  });

  return container;
}

export function finishConfiguratorEmbed(configId, status) {
  const container = document.getElementById(`cfg-embed-${configId}`);
  if (!container) return;
  container.classList.add("done");
  const msg = status === "done"
    ? '<div class="cfg-done-msg">&#10003; Product added to quote</div>'
    : '<div class="cfg-done-msg cfg-cancelled">Configuration cancelled</div>';
  container.innerHTML = msg;
  state.cfgConfigId = null;
  scrollChat();
}

// ---------------------------------------------------------------------------
// Interactive Configurator Card
// ---------------------------------------------------------------------------

export function createConfiguratorCard(model, configId, productName, partNumber) {
  const card = document.createElement("div");
  card.className = "cfg-card";
  card.id = `cfg-card-${configId}`;
  card.dataset.configId = configId;
  card._cfgProductName = productName;
  card._cfgPartNumber = partNumber;

  const header = document.createElement("div");
  header.className = "cfg-card-header";
  header.innerHTML = _cfgHeaderHtml(model, productName, partNumber);
  card.appendChild(header);

  // Tabs (only if >1)
  if (model.tabs && model.tabs.length > 1) {
    const tabBar = document.createElement("div");
    tabBar.className = "cfg-tabs";
    for (const t of model.tabs) {
      const btn = document.createElement("button");
      btn.className = "cfg-tab-btn" + (t.is_active ? " active" : "");
      btn.textContent = t.name;
      btn.addEventListener("click", () => {
        _cfgSendAction(configId, { action: "change_tab", tab_name: t.name });
      });
      tabBar.appendChild(btn);
    }
    card.appendChild(tabBar);
  }

  const body = document.createElement("div");
  body.className = "cfg-card-body";
  for (const a of model.attributes) {
    body.appendChild(_cfgCreateAttrRow(a, configId));
  }
  card.appendChild(body);

  const msgArea = document.createElement("div");
  msgArea.className = "cfg-messages";
  _cfgRenderMessages(msgArea, model.messages || []);
  card.appendChild(msgArea);

  const footer = document.createElement("div");
  footer.className = "cfg-footer";

  const cancelBtn = document.createElement("button");
  cancelBtn.className = "btn";
  cancelBtn.textContent = "Cancel";
  cancelBtn.addEventListener("click", () => {
    _cfgSendAction(configId, { action: "cancel" });
  });

  const addBtn = document.createElement("button");
  addBtn.className = "btn btn-primary cfg-add-btn";
  addBtn.textContent = "Add to Quote";
  addBtn.disabled = !model.can_add_to_quote;
  if (!model.can_add_to_quote) {
    addBtn.title = "Complete all required attributes first";
  }
  addBtn.addEventListener("click", () => {
    if (!addBtn.disabled) {
      _cfgSendAction(configId, { action: "add_to_quote" });
    }
  });

  footer.append(cancelBtn, addBtn);
  card.appendChild(footer);

  return card;
}

function _cfgHeaderHtml(model, productName, partNumber) {
  const price = model.price;
  const prog = model.progress || { required_total: 0, required_complete: 0 };
  const pct = prog.required_total > 0
    ? Math.round((prog.required_complete / prog.required_total) * 100) : 0;
  const priceStr = price
    ? `${esc(price.currency || "$")} ${esc(price.total_price || price.base_price || "")}`
    : "";
  return `
    <div class="cfg-header-top">
      <div class="cfg-header-title">
        <span class="cfg-gear-icon">&#9881;</span>
        <strong>${esc(productName || model.product_name || "Product")}</strong>
        ${partNumber ? `<span class="cfg-part-number">${esc(partNumber)}</span>` : ""}
      </div>
      <div class="cfg-header-meta">
        ${priceStr ? `<span class="cfg-header-price">${priceStr}</span>` : ""}
        <span class="cfg-header-progress">${prog.required_complete}/${prog.required_total} required</span>
      </div>
    </div>
    <div class="cfg-progress-bar-track">
      <div class="cfg-progress-bar-fill" style="width: ${pct}%"></div>
    </div>
  `;
}

function _cfgCreateAttrRow(a, configId) {
  const row = document.createElement("div");
  row.className = "cfg-attr-row";
  row.dataset.attrName = a.name;

  if (!a.is_enabled) row.classList.add("disabled");
  if (a.is_incomplete) row.classList.add("incomplete");
  if (a.has_conflict) row.classList.add("conflict");

  const label = document.createElement("div");
  label.className = "cfg-attr-label";
  label.innerHTML = `${esc(a.label || a.name)}${a.is_required ? '<span class="cfg-req">*</span>' : ""}`;
  row.appendChild(label);

  const control = document.createElement("div");
  control.className = "cfg-control";

  const displayAs = a.display_as;
  const opts = (a.options || []);
  const enabled = a.is_enabled !== false;

  // 96=HiddenCalc, 97=ReadOnly
  if (displayAs === 96 || displayAs === 97) {
    const span = document.createElement("span");
    span.className = "cfg-readonly-value";
    span.textContent = a.current || "";
    control.appendChild(span);
  }
  // 3=Dropdown, or 4=Radio with >5 options, or 5=Listbox
  else if (displayAs === 3 || (displayAs === 4 && opts.length > 5) || displayAs === 5) {
    const sel = document.createElement("select");
    sel.className = "cfg-select";
    sel.disabled = !enabled;

    const emptyOpt = document.createElement("option");
    emptyOpt.value = "";
    emptyOpt.textContent = "-- select --";
    emptyOpt.disabled = true;
    emptyOpt.selected = !opts.some(o => o.selected);
    sel.appendChild(emptyOpt);

    for (const o of opts) {
      const opt = document.createElement("option");
      opt.value = o.display || o.code;
      opt.textContent = o.display || o.code;
      if (o.price) opt.textContent += ` (${o.price})`;
      opt.selected = o.selected;
      opt.disabled = o.disabled;
      sel.appendChild(opt);
    }
    sel.addEventListener("change", () => {
      if (sel.value) {
        _cfgSendAction(configId, { action: "set_attribute", attribute_name: a.name, value: sel.value });
        row.classList.add("updating");
      }
    });
    control.appendChild(sel);
  }
  // 4=Radio (<=5 options)
  else if (displayAs === 4 && opts.length <= 5) {
    const group = document.createElement("div");
    group.className = "cfg-radio-group";
    const groupName = `cfg-radio-${configId}-${a.name}`;
    for (const o of opts) {
      const lbl = document.createElement("label");
      lbl.className = "cfg-radio-label" + (o.disabled ? " disabled" : "");
      const inp = document.createElement("input");
      inp.type = "radio";
      inp.name = groupName;
      inp.value = o.display || o.code;
      inp.checked = o.selected;
      inp.disabled = !enabled || o.disabled;
      inp.addEventListener("change", () => {
        _cfgSendAction(configId, { action: "set_attribute", attribute_name: a.name, value: inp.value });
        row.classList.add("updating");
      });
      lbl.append(inp, document.createTextNode(` ${o.display || o.code}`));
      if (o.price) {
        const priceSpan = document.createElement("span");
        priceSpan.className = "cfg-option-price";
        priceSpan.textContent = ` ${o.price}`;
        lbl.appendChild(priceSpan);
      }
      group.appendChild(lbl);
    }
    control.appendChild(group);
  }
  // 2=Checkbox
  else if (displayAs === 2) {
    const group = document.createElement("div");
    group.className = "cfg-checkbox-group";
    for (const o of opts) {
      const lbl = document.createElement("label");
      lbl.className = "cfg-checkbox-label" + (o.disabled ? " disabled" : "");
      const inp = document.createElement("input");
      inp.type = "checkbox";
      inp.value = o.display || o.code;
      inp.checked = o.selected;
      inp.disabled = !enabled || o.disabled;
      inp.addEventListener("change", () => {
        _cfgSendAction(configId, { action: "set_attribute", attribute_name: a.name, value: inp.value });
        row.classList.add("updating");
      });
      lbl.append(inp, document.createTextNode(` ${o.display || o.code}`));
      if (o.price) {
        const priceSpan = document.createElement("span");
        priceSpan.className = "cfg-option-price";
        priceSpan.textContent = ` ${o.price}`;
        lbl.appendChild(priceSpan);
      }
      group.appendChild(lbl);
    }
    control.appendChild(group);
  }
  // 6=Image button
  else if (displayAs === 6) {
    const group = document.createElement("div");
    group.className = "cfg-tile-group";
    for (const o of opts) {
      const tile = document.createElement("button");
      tile.className = "cfg-tile" + (o.selected ? " selected" : "") + (o.disabled ? " disabled" : "");
      tile.textContent = o.display || o.code;
      tile.disabled = !enabled || o.disabled;
      tile.addEventListener("click", () => {
        _cfgSendAction(configId, { action: "set_attribute", attribute_name: a.name, value: o.display || o.code });
        row.classList.add("updating");
      });
      group.appendChild(tile);
    }
    control.appendChild(group);
  }
  // 95=Free input
  else if (displayAs === 95) {
    const inp = document.createElement("input");
    inp.type = "text";
    inp.className = "cfg-text-input";
    inp.value = a.current || "";
    inp.disabled = !enabled;
    inp.placeholder = a.hint || "";
    const commit = () => {
      const val = inp.value.trim();
      if (val !== (a.current || "")) {
        _cfgSendAction(configId, { action: "set_attribute", attribute_name: a.name, value: val });
        row.classList.add("updating");
      }
    };
    inp.addEventListener("blur", commit);
    inp.addEventListener("keydown", (e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } });
    control.appendChild(inp);
  }
  // Fallback
  else {
    const span = document.createElement("span");
    span.className = "cfg-readonly-value";
    span.textContent = a.current || "(not set)";
    control.appendChild(span);
  }

  row.appendChild(control);

  // Spinner overlay
  const spinner = document.createElement("div");
  spinner.className = "cfg-row-spinner";
  spinner.innerHTML = '<div class="cfg-spinner-dot"></div>';
  row.appendChild(spinner);

  return row;
}

function _cfgRenderMessages(container, messages) {
  container.innerHTML = "";
  for (const m of messages) {
    const div = document.createElement("div");
    div.className = `cfg-msg cfg-msg-${m.level || "info"}`;
    div.textContent = m.text;
    container.appendChild(div);
  }
}

export function updateConfiguratorCard(configId, model, changedAttr) {
  const card = document.getElementById(`cfg-card-${configId}`);
  if (!card) return;

  const header = card.querySelector(".cfg-card-header");
  if (header) {
    const prodName = card._cfgProductName || model.product_name || "Product";
    const partNum = card._cfgPartNumber || "";
    header.innerHTML = _cfgHeaderHtml(model, prodName, partNum);
  }

  const tabBar = card.querySelector(".cfg-tabs");
  if (tabBar && model.tabs) {
    const btns = tabBar.querySelectorAll(".cfg-tab-btn");
    btns.forEach((btn, i) => {
      if (model.tabs[i]) {
        btn.classList.toggle("active", model.tabs[i].is_active);
      }
    });
  }

  const body = card.querySelector(".cfg-card-body");
  if (body) {
    body.innerHTML = "";
    for (const a of model.attributes) {
      const row = _cfgCreateAttrRow(a, configId);
      if (changedAttr && a.name === changedAttr) {
        row.classList.add("changed");
        setTimeout(() => row.classList.remove("changed"), 600);
      }
      body.appendChild(row);
    }
  }

  const msgArea = card.querySelector(".cfg-messages");
  if (msgArea) {
    _cfgRenderMessages(msgArea, model.messages || []);
  }

  const addBtn = card.querySelector(".cfg-add-btn");
  if (addBtn) {
    addBtn.disabled = !model.can_add_to_quote;
    addBtn.title = model.can_add_to_quote ? "" : "Complete all required attributes first";
  }

  scrollChat();
}

export function updateConfiguratorCardError(configId, error) {
  const card = document.getElementById(`cfg-card-${configId}`);
  if (!card) return;
  const msgArea = card.querySelector(".cfg-messages");
  if (msgArea) {
    _cfgRenderMessages(msgArea, [{ text: error, level: "error" }]);
  }
  card.querySelectorAll(".cfg-attr-row.updating").forEach(r => r.classList.remove("updating"));
}

export function finishConfiguratorCard(configId, status) {
  const card = document.getElementById(`cfg-card-${configId}`);
  if (!card) return;

  card.classList.add("done");

  const body = card.querySelector(".cfg-card-body");
  const msgArea = card.querySelector(".cfg-messages");
  const footer = card.querySelector(".cfg-footer");

  const traceEl = card.querySelector(".cfg-trace-panel");
  if (traceEl) traceEl.remove();

  if (status === "done") {
    if (body) body.innerHTML = '<div class="cfg-done-msg">&#10003; Product added to quote</div>';
    if (msgArea) msgArea.innerHTML = "";
    if (footer) footer.remove();
  } else {
    if (body) body.innerHTML = '<div class="cfg-done-msg cfg-cancelled">Configuration cancelled</div>';
    if (msgArea) msgArea.innerHTML = "";
    if (footer) footer.remove();
  }
  scrollChat();
}

// ---------------------------------------------------------------------------
// Legacy configurator table (text-only fallback)
// ---------------------------------------------------------------------------

export function createConfiguratorTable(data) {
  const attrs = data.attributes || [];
  if (attrs.length === 0) return "<p>No attributes available.</p>";

  let priceHtml = "";
  if (data.price) {
    priceHtml = `<div class="cfg-price">${esc(data.price.currency || "$")} ${esc(data.price.total_price || data.price.base_price || "")}</div>`;
  }

  let rows = "";
  for (const a of attrs) {
    const req = a.is_required ? '<span class="cfg-req">*</span>' : "";
    const enabled = a.is_enabled !== false;
    const rowClass = enabled ? "" : " cfg-disabled";

    let valueCell = "";
    if (a.options && a.options.length > 0) {
      const optHtml = a.options.map(o => {
        const sel = o.selected ? " cfg-opt-selected" : "";
        const display = esc(o.display || o.code || "");
        return `<span class="cfg-opt${sel}" data-attr="${escAttr(a.name)}" data-val="${display}">${display}</span>`;
      }).join("");
      valueCell = `<div class="cfg-opts">${optHtml}</div>`;
    } else {
      valueCell = `<span class="cfg-current">${esc(String(a.current || "(not set)"))}</span>`;
    }

    rows += `<tr class="${rowClass}">
      <td class="cfg-name">${esc(a.name)}${req}</td>
      <td class="cfg-type">${esc(a.display_type || "")}</td>
      <td class="cfg-value">${valueCell}</td>
    </tr>`;
  }

  return `
    ${priceHtml}
    <table class="cfg-table">
      <thead><tr><th>Attribute</th><th>Type</th><th>Value / Options</th></tr></thead>
      <tbody>${rows}</tbody>
    </table>
    <div class="cfg-hint">Use <code>set &lt;name&gt; = &lt;value&gt;</code> to configure, then <code>done</code> to add to quote.</div>
  `;
}

// ---------------------------------------------------------------------------
// Action queue — one in flight at a time
// ---------------------------------------------------------------------------

function _cfgSendAction(configId, payload) {
  const msg = {
    type: "configurator_action",
    conversation_id: state.activeSessionId,
    configuration_id: configId,
    ...payload,
  };

  if (state.cfgInFlight) {
    state.cfgPending.push(msg);
    return;
  }

  state.cfgInFlight = true;
  wsSend(msg);
}

// ---------------------------------------------------------------------------
// Quote iframe in bottom panel
// ---------------------------------------------------------------------------

export async function mountQuoteInPanel(quoteId, quoteNumber, status) {
  // v2: the Quote view's #panel-quote-frame (and its #quoteFrameBody/
  // #quoteFrameTitle children) may not exist yet if the user never visited
  // the view -- it's created lazily, on first activation (shell-v2.js's
  // showViewV2() "quote" branch, id-reuse pattern mirroring Traces/Errors).
  // Calling showViewV2("quote") here both creates+populates it on demand AND
  // makes the Quote view the active one, covering the v1
  // "expandZone('bottom'); showPanel('quote-frame');" auto-show behavior
  // below in one call. Dynamic import (not a static one) so this
  // v1-reachable, always-loaded module doesn't pull the whole v2 shell graph
  // into every page's bundle -- mirrors tenants.js's own resetViewGuards()
  // import, which uses a dynamic import for the identical reason (there it's
  // to avoid a cycle; here it's to avoid an eager-load regression for v1-only
  // page loads, since shell-v2.js has no reason to ever load there).
  const isV2 = !!document.getElementById("appV2");
  if (isV2) {
    const { showViewV2 } = await import("./shell-v2.js");
    showViewV2("quote");
  }

  const body = document.getElementById('quoteFrameBody');
  if (!body) return;

  body.innerHTML = '';

  const iframe = document.createElement('iframe');
  iframe.className = 'cfg-iframe';
  iframe.id = 'quoteFrameIframe';
  iframe.src = `/quotation/Cart.aspx?quoteId=${encodeURIComponent(quoteId)}`;
  iframe.setAttribute('frameborder', '0');
  iframe.setAttribute('allowfullscreen', 'true');
  body.appendChild(iframe);

  const title = document.getElementById('quoteFrameTitle');
  if (title) title.textContent = `Quote ${quoteNumber || quoteId}${status ? ' \u2014 ' + status : ''}`;

  if (!isV2) {
    // Force-open bottom panel and switch to Quote Frame tab (v1 zone system).
    // v2: showViewV2("quote") above already created the container, populated
    // the chrome, and made the view active -- nothing left to do here.
    expandZone('bottom');
    showPanel('quote-frame');
  }

  state.qteQuoteId = String(quoteId);
}

// Deterministic E2E hook (mirrors the window.__test_X convention already
// used by timeline-view.js/sync-view.js/tables-view.js/templates-view.js/
// conversations-view.js/database-view.js/promotion-wizard.js/pipeline.js) --
// lets a v2 E2E test drive the "chat/tool result opens a quote" path without
// a real SAP CPQ session: calls the exact same mountQuoteInPanel() a live
// quote_opened tool result would (chat.js).
window.__test_mountQuoteInPanel = mountQuoteInPanel;

export function resetQuotePanel() {
  const body = document.getElementById('quoteFrameBody');
  if (body) {
    body.innerHTML = '<div class="quote-frame-placeholder">No quote loaded. Use chat to open a quote.</div>';
  }
  const title = document.getElementById('quoteFrameTitle');
  if (title) title.textContent = 'Active Quote Frame';

  state.qteQuoteId = null;
}

/**
 * Mount a configurator element (iframe embed or interactive card) into the
 * dedicated Configurator panel (bottom zone), so it lives beside the chat
 * (right zone) and the Console/traces — instead of taking over the chat
 * stream. Returns true on success.
 */
export function mountConfiguratorInPanel(el) {
  const container = getPanelContainer('configurator');
  if (!container) return false;
  container.replaceChildren(el);
  expandZone('bottom');
  showPanel('configurator');
  return true;
}

export function resetConfiguratorPanel() {
  const container = getPanelContainer('configurator');
  if (container) {
    container.innerHTML = '<div class="quote-frame-placeholder">No product open. Ask chat to configure a product.</div>';
  }
}

export function cfgFlushQueue() {
  if (state.cfgPending.length > 0 && !state.cfgInFlight) {
    const next = state.cfgPending.shift();
    state.cfgInFlight = true;
    wsSend(next);
  }
}
